import { useState, useEffect, useCallback } from 'react'
import { usePrescriptionTemplates } from './usePrescriptionTemplates'
import { CaseStatusEnum, TELEMEDICINE_CATEGORIES } from '../constants'
import { useCaseViewContext } from '../context/CaseViewContext'

export const usePrescriptionQueue = ({ 
    caseDetails, 
    activeRequest, 
    apiClasses,
    apiType = 'physician',
    currentPharmacyId,
    setChartingPrescriptions,
}) => {
    const { readOnlyMode } = useCaseViewContext()
    const [prescriptionQueue, setPrescriptionQueue] = useState({})
    const [unTemplatizedPrescriptions, setUnTemplatizedPrescriptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [prescriptionQueueArray, setPrescriptionQueueArray] = useState([])
    const [allPrescriptionsValid, setAllPrescriptionsValid] = useState(false)
    
    const { templatizePrescription, dispenseUnits } = usePrescriptionTemplates({ apiClasses, apiType })
    
    const initializeQueue = useCallback(async () => {
        if (apiType === 'admin' || readOnlyMode || caseDetails?.status === CaseStatusEnum.REFER) {
            setPrescriptionQueue({})
            setUnTemplatizedPrescriptions([])
            return
        }
        
        if (!activeRequest?.medicines?.length) {
            setPrescriptionQueue({})
            setUnTemplatizedPrescriptions([])
            return
        }

        if (!dispenseUnits?.length) {
            return
        }
        
        setLoading(true)
        const queuedPrescriptions = {}
        const unTemplatized = []
        
        try {
            const completedStatuses = [CaseStatusEnum.CONSULT_COMPLETE, CaseStatusEnum.CONSULT_COMPLETE_NO_RX]
            const isCaseCompleted = completedStatuses.includes(caseDetails?.status)
            
            if (isCaseCompleted) {
                setPrescriptionQueue({})
                setUnTemplatizedPrescriptions([])
                setLoading(false)
                return
            }
            
            const categoriesToNotBeQueued = [
                TELEMEDICINE_CATEGORIES.TESTOSTERONE.categoryId,
            ]
            
            const caseShouldNotBeQueued = categoriesToNotBeQueued.includes(caseDetails?.category?._id?.toString())
            
            if (caseShouldNotBeQueued) {
                setPrescriptionQueue({})
                setUnTemplatizedPrescriptions([])
                setLoading(false)
                return
            }
            
            for (const medication of activeRequest.medicines) {
                if (!apiClasses?.PhysicianAPI) {
                    return console.warn('PhysicianAPI not available in apiClasses')
                }
                
                try {
                    const templatizedPrescription = await templatizePrescription({
                        requestedMedication: medication,
                        categoryId: caseDetails?.category?._id,
                        stateId: caseDetails?.stateId,
                        pharmacyId: currentPharmacyId,
                    })
                    
                    if (templatizedPrescription) {
                        queuedPrescriptions[medication._id] = templatizedPrescription
                    } else {
                        unTemplatized.push(medication)
                    }
                } catch (error) {
                    console.error(`Error templatizing prescription for ${medication.drugName}:`, error)
                    unTemplatized.push(medication)
                }
            }
        } catch (error) {
            console.error('Error initializing prescription queue:', error)
        } finally {
            setPrescriptionQueue(queuedPrescriptions)
            setUnTemplatizedPrescriptions(unTemplatized)
            setLoading(false)
        }
    }, [dispenseUnits, activeRequest, apiClasses, caseDetails, caseDetails?.status, apiType])
    
    const addPrescription = useCallback(async (prescription) => {
        if (apiType === 'admin') {
            return
        }

        if (!apiClasses?.PrescriptionsAPI) {
            console.error('PrescriptionsAPI not available in apiClasses')
            return
        }
        
        const prescriptionWithId = {
            ...prescription,
            _id: prescription._id || Date.now()
        }
        
        if (prescriptionWithId.pharmacyId && !prescriptionWithId.pharmacy) {
            try {
                const { data: { pharmacy } } = await apiClasses.PrescriptionsAPI.getPharmacybyId(prescriptionWithId.pharmacyId)
                prescriptionWithId.pharmacy = pharmacy
            } catch (error) {
                console.error('Error fetching pharmacy details:', error)
            }
        }
        
        setPrescriptionQueue(prev => ({
            ...prev,
            [prescriptionWithId._id]: prescriptionWithId
        }))
    }, [apiClasses, apiType])
    
    const updatePrescription = useCallback((prescriptionId, updates) => {
        if (apiType === 'admin') {
            return
        }
        
        setPrescriptionQueue(prev => {
            const updated = {
                ...prev,
                [prescriptionId]: {
                    ...prev[prescriptionId],
                    ...updates
                }
            }
            return updated
        })
    }, [apiType])
    
    const removePrescription = useCallback((prescriptionId) => {
        if (apiType === 'admin') {
            return
        }
        
        setPrescriptionQueue(prev => {
            const newQueue = { ...prev }
            delete newQueue[prescriptionId]
            return newQueue
        })
    }, [apiType])
    
    const clearQueue = useCallback(() => {
        if (apiType === 'admin') {
            return
        }
        
        setPrescriptionQueue({})
        setUnTemplatizedPrescriptions([])
    }, [apiType])
        
    useEffect(() => {
        initializeQueue()
    }, [initializeQueue])

    useEffect(() => {
        const queueArray = Object.values(prescriptionQueue)
        setPrescriptionQueueArray(queueArray)
        setChartingPrescriptions(prescriptionQueue)
        setAllPrescriptionsValid(queueArray.every(prescription => prescription.valid))
    }, [prescriptionQueue])
    
    return {
        prescriptionQueue: prescriptionQueue,
        prescriptionQueueArray,
        unTemplatizedPrescriptions,
        loading,
        allPrescriptionsValid,
        initializeQueue,
        addPrescription,
        updatePrescription,
        removePrescription,
        clearQueue
    }
}
